import React, {useEffect, useState} from 'react';
import {useParams} from "react-router";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import projectUtils from "../../../../utils/project-utils.js";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaPlus} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import acceptanceRulesService from "../../../../services/web/acceptance-rules-service.js";
import acceptanceRulesClassification from "./utils/acceptance-rules-classification.js";
import useFetchData from "../hooks/use-fetch-data.js";
import SelectComponent from "../../../../components/select/select-component.jsx";
import InputRadio from "../../../../components/input/input-radio.jsx";
import groupService from "../../../../services/web/group-service.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import SentryRow from "../../../../components/template/sentry-row.jsx";

const initialState = {
    reason: null,
    city: null,
    classifications_monitor: [],
    monitor_output: null,
    rule: null,
    visibility: null,
    userGroup: null
}

const AcceptanceRulesForm = () => {
    const {id} = useParams();
    const navigate = navigateHook();

    const [state, setState] = useState(initialState);
    const [isLoading, setIsLoading] = useState(false);

    const fetchData = useFetchData();

    const {data, refetch} = useQuery({
        queryFn: () => acceptanceRulesService.get(id),
        queryKey: ['rules'],
        enabled: !!id
    });

    const {data: groups} = useQuery({
        queryFn: () => groupService.getOptionsMyUser("occurrence"),
        queryKey: ['groups'],
    })

    useEffect(() => {
        if (!!id && data) {
            const {reason, city, classifications_monitor, monitor_output, rule, visibility, userGroup} = data;

            setState({
                reason,
                city,
                classifications_monitor: acceptanceRulesClassification.toOption(classifications_monitor),
                monitor_output,
                rule,
                visibility: visibility?.startsWith("G") ? "G" : visibility,
                userGroup
            });
        } else  {
            setState(initialState);
        }
    }, [id, data]);

    const submit = () => {
        setIsLoading(true)

        const formData = new FormData();
        formData.append("reason", state.reason);
        formData.append("city", state.city);
        formData.append("monitor_output", state.monitor_output);
        formData.append("rule", state.rule);
        formData.append("visibility", state.visibility);
        formData.append("userGroup", state.userGroup);

        state.classifications_monitor.forEach(c => formData.append("classifications_monitor[]", c.value))

        let response;
        if (id) {
            formData.append("_method", "PUT")
            response = acceptanceRulesService.edit(id, formData);
        }
        else response = acceptanceRulesService.create(formData);

        response
            .then(({data}) => {
                projectUtils.success(!id);

                if (id) {
                    refetch();
                    return;
                }

                const urlDev = `/acceptance-rules/form/${data.id}`;
                navigate(urlDev);
            })
            .catch(error => {
                setIsLoading(false)
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false));
    }

    const deleteGroup = () => {
        projectUtils.swalMessage(
            "warning",
            "Excluir",
            `Excluir a regra de monitoramento automático "${id}"?`,
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (isConfirmed) {
                    acceptanceRulesService.delete(id)
                        .then(() => {
                            projectUtils.success(true, "Excluído com sucesso");
                            navigate("/acceptance-rules");
                        })
                        .catch(error => {
                            console.error(error);
                            let message = 'Não foi possível excluir.';
                            if (error.response.status !== 500) message = error.response.data.message;

                            projectUtils.swalErrorMessage('', message, 'Ok');
                        });
                }
            }
        );
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Regra de monitoramento automático</Template.Title>

                {
                    id &&
                    <Button target={"default"} onClick={() => navigate("/acceptance-rules/form")}>
                        <FaPlus/> Adicionar regra
                    </Button>
                }
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <div className={"col-span-12 bg-gray-200 rounded-md text-center py-5"}>
                                <p className={"!m-0 text-xl font-bold"}>
                                    Monitorar automaticamente as ocorrências recebidas de outras instituições e aceitas, de acordo com os parâmetros definidos a seguir
                                </p>
                            </div>

                            <SelectComponent
                                classNameWrapper={"col-span-6"}
                                value={state.reason}
                                options={fetchData.reasons}
                                label={"Natureza *"}
                                onChange={(value) => setState(prev => ({...prev, reason: value?.value}))}
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-6"}
                                value={state.classifications_monitor}
                                options={acceptanceRulesClassification.options}
                                label={"Classificação *"}
                                onChange={(value) => setState(prev => ({...prev, classifications_monitor: value}))}
                                isMulti
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-6"}
                                value={state.city}
                                options={fetchData.city}
                                label={"Município *"}
                                onChange={(value) => setState(prev => ({...prev, city: value?.value}))}
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-6"}
                                value={state.monitor_output}
                                options={[
                                    {value: "TRACKING", label: "Acompanhamento"},
                                    {value: "ALARM", label: "Alarme"}
                                ]}
                                label={"Tipo de monitoramento *"}
                                onChange={(value) => setState(prev => ({...prev, monitor_output: value?.value, visibility: value?.value === "ALARM" ? "PL" : prev.visibility}))}
                            />
                        </SentryRow>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Header>
                        <Card.Title>Regra de aceite de novas ocorrências</Card.Title>
                    </Card.Header>

                    <Card.Body className={"grid grid-cols-12 gap-3"}>
                        <section className={"col-span-3 space-y-2"}>
                            <InputRadio
                                id={"automatically-accept"}
                                label={"Aceitar automaticamente"}
                                checked={state.rule === "OCCURRENCE_ACCEPT"}
                                onChange={() => setState(prev => ({...prev, rule: "OCCURRENCE_ACCEPT"}))}
                            />

                            <InputRadio
                                id={"user-accept"}
                                label={"Depende de ação do agente"}
                                checked={state.rule === "OCCURRENCE_PENDING"}
                                onChange={() => setState(prev => ({...prev, rule: "OCCURRENCE_PENDING"}))}
                            />
                        </section>

                        {
                            state.rule === "OCCURRENCE_ACCEPT" &&
                            <SentryRow className={"col-span-9"}>
                                <SelectComponent
                                    value={state.visibility}
                                    options={[
                                        {value: "PL", label: "Pública Local"},
                                        {value: "TIL", label: "Inteligência Local"},
                                        {value: "G", label: "Somente o meu grupo de operação"}
                                    ]}
                                    label={"Visibilidade *"}
                                    isDisabled={state.monitor_output === "ALARM"}
                                    classNameWrapper={"col-span-6"}
                                    onChange={(value) => setState(prev => ({...prev, visibility: value?.value}))}
                                />

                                {
                                    state.visibility === "G" &&
                                    <SelectComponent
                                        value={state.userGroup}
                                        options={groups ?? []}
                                        label={"Grupo de operação *"}
                                        classNameWrapper={"col-span-6"}
                                        onChange={(value) => setState(prev => ({...prev, userGroup: value?.value}))}
                                    />
                                }
                            </SentryRow>
                        }
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body className={"flex justify-end gap-2"}>
                        {
                            id &&
                            <Button target={"danger"} onClick={deleteGroup} actionFeedbackType='delete'>
                                Excluir
                            </Button>
                        }
                        <Button onClick={submit} actionFeedbackType='save'>
                            Salvar
                        </Button>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default AcceptanceRulesForm;